/**
 * 会員登録フォーム（#member-register-form）のクライアント側バリデーション
 * DOM は visit と共通（visit__fieldError 等）。visit-form-validation.js と同様の挙動。
 */
import { isValidPhoneTel, PHONE_TEL_FORMAT_MESSAGE } from './phone-validation.js';
import { isValidEmail } from './email-validation.js';
/** テンプレの pattern / maxlength に合わせた半角英数字 1〜8 文字 */
const MEMBER_PASSWORD_RE = /^[0-9A-Za-z]{1,8}$/;

function trimValue(id) {
	const el = document.getElementById(id);
	return el ? el.value.trim() : '';
}

function clearErrorForControl(form, controlId) {
	form.querySelectorAll(`.visit__fieldError[data-for="${controlId}"]`).forEach((n) => n.remove());
	const control = document.getElementById(controlId);
	if (!control) return;
	control.removeAttribute('aria-invalid');
	control.classList.remove('is-invalid');
	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) wrap.classList.remove('is-invalid');
	const schedule = control.closest('.visit__schedule');
	if (schedule) schedule.classList.remove('is-invalid');
	const consent = control.closest('.visit__consent');
	if (consent) consent.classList.remove('is-invalid');
}

export function clearMemberRegisterFormErrors(form) {
	form.querySelectorAll('.visit__fieldError').forEach((n) => n.remove());
	form.querySelectorAll('.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__inputWrap--date.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__schedule.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__consent.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('[aria-invalid="true"]').forEach((n) => n.removeAttribute('aria-invalid'));
}

function mountErrorParagraph(controlId, message) {
	const p = document.createElement('p');
	p.className = 'visit__fieldError';
	p.setAttribute('data-for', controlId);
	p.setAttribute('role', 'alert');
	p.textContent = message;
	return p;
}

function showFieldError(form, controlId, message) {
	const control = document.getElementById(controlId);
	if (!control) return;

	clearErrorForControl(form, controlId);

	control.setAttribute('aria-invalid', 'true');
	control.classList.add('is-invalid');

	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) {
		wrap.classList.add('is-invalid');
		wrap.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	const schedule = control.closest('.visit__schedule');
	if (schedule) {
		schedule.classList.add('is-invalid');
		schedule.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	if (control.type === 'checkbox' && control.id === 'member-privacy') {
		const consent = control.closest('.visit__consent');
		if (consent) {
			consent.classList.add('is-invalid');
			consent.appendChild(mountErrorParagraph(controlId, message));
		}
		return;
	}

	if (control.id === 'member-name-family' || control.id === 'member-name-given') {
		const grid = control.closest('.visit__fieldGrid--name');
		if (grid) {
			grid.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
			return;
		}
	}

	if (control instanceof HTMLFieldSetElement && control.id === 'member-pref-area-fieldset') {
		control.appendChild(mountErrorParagraph(controlId, message));
		return;
	}

	control.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
}

function focusFirst(controlId) {
	const el = document.getElementById(controlId);
	if (!el) return;
	if (el instanceof HTMLFieldSetElement) {
		const first = el.querySelector('input[type="checkbox"]');
		if (first instanceof HTMLElement) {
			first.focus();
		}
		return;
	}
	el.focus();
}

/**
 * @returns {boolean}
 */
export function validateMemberRegisterForm(form) {
	clearMemberRegisterFormErrors(form);

	const errors = [];

	const family = trimValue('member-name-family');
	const given = trimValue('member-name-given');
	if (!family && !given) {
		errors.push(['member-name-family', '性・名を入力してください。']);
	} else if (!family) {
		errors.push(['member-name-family', '性を入力してください。']);
	} else if (!given) {
		errors.push(['member-name-given', '名を入力してください。']);
	}

	const telRaw = trimValue('member-tel');
	if (telRaw && !isValidPhoneTel(telRaw)) {
		errors.push(['member-tel', PHONE_TEL_FORMAT_MESSAGE]);
	}

	const email = trimValue('member-email');
	if (!email) {
		errors.push(['member-email', 'メールアドレスを入力してください。']);
	} else if (!isValidEmail(email)) {
		errors.push(['member-email', 'メールアドレスの形式が正しくありません。']);
	}

	const pwd = trimValue('member-password');
	const pwd2 = trimValue('member-password-confirm');
	if (!pwd) {
		errors.push(['member-password', 'パスワードを入力してください。']);
	} else if (!MEMBER_PASSWORD_RE.test(pwd)) {
		errors.push(['member-password', 'パスワードは半角英数字1〜8文字で入力してください。']);
	}

	if (!errors.some(([id]) => id === 'member-password')) {
		if (!pwd2) {
			errors.push(['member-password-confirm', '確認のためパスワードを入力してください。']);
		} else if (pwd !== pwd2) {
			errors.push(['member-password-confirm', 'パスワードが一致しません。']);
		}
	}

	const areaChecked = form.querySelectorAll('input[name="member_pref_area[]"]:checked');
	if (!areaChecked.length) {
		errors.push(['member-pref-area-fieldset', 'ご希望エリアを1つ以上選択してください。']);
	}

	const privacy = form.querySelector('#member-privacy');
	if (!privacy || !privacy.checked) {
		errors.push(['member-privacy', 'プライバシーポリシーに同意してください。']);
	}

	if (errors.length === 0) {
		return true;
	}

	for (const [id, msg] of errors) {
		showFieldError(form, id, msg);
	}
	focusFirst(errors[0][0]);
	return false;
}

export function initMemberRegisterFormValidation(form) {
	if (!form || form.id !== 'member-register-form') return;

	const onEdit = (e) => {
		const t = e.target;
		if (!t || !form.contains(t)) return;
		if (t.id) {
			clearErrorForControl(form, t.id);
		}
		if (t.matches?.('input[name="member_pref_area[]"]')) {
			clearErrorForControl(form, 'member-pref-area-fieldset');
		}
		if (t.id === 'member-password' || t.id === 'member-password-confirm') {
			clearErrorForControl(form, 'member-password');
			clearErrorForControl(form, 'member-password-confirm');
		}
	};

	form.addEventListener('input', onEdit);
	form.addEventListener('change', onEdit);
}
